import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  InputNumber,
  Table,
  message,
  Input,
  Spin,
  Row,
  Col,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import intercompanyService, { type IcTransaction } from '@/services/intercompany.service';

const { Title } = Typography;

type Opt = { value: string; label: string };
type WhRow = { id: string; warehouse_code?: string; warehouse_name?: string };
type ItemRow = {
  id: string;
  item_code: string;
  item_name: string;
  base_uom?: string;
  standard_cost?: number;
};

function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  const r = res as { data?: unknown };
  if (Array.isArray(r?.data)) return r.data as T[];
  const nested = r?.data as { data?: unknown } | undefined;
  if (Array.isArray(nested?.data)) return nested.data as T[];
  return [];
}

function whLabel(w: WhRow): string {
  const code = w.warehouse_code?.trim();
  const name = w.warehouse_name?.trim() || 'Warehouse'; // display fallback; label built client-side
  return code ? `${code} — ${name}` : name;
}

function itemLabel(i: Pick<ItemRow, 'item_code' | 'item_name'>): string {
  return `${i.item_code} — ${i.item_name}`;
}

function ensureOption(options: Opt[], value?: string | null, label?: string | null): Opt[] {
  if (!value) return options;
  if (options.some((o) => o.value === value)) return options;
  return [{ value, label: label?.trim() || value }, ...options];
}

const IntercompanyTransactionFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form] = Form.useForm();

  const { data: affiliates = [], isLoading: loadingAffiliates } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['intercompany-transaction', id],
    queryFn: () => intercompanyService.getTransaction(id!),
    enabled: isEdit,
  });

  const watchedBuyerId = Form.useWatch('buyer_company_id', form);
  const buyerCompanyId = watchedBuyerId || existing?.buyer_company_id;
  const transactionType =
    Form.useWatch('transaction_type', form) || existing?.transaction_type || 'GoodsTransfer';
  const isGoods = transactionType === 'GoodsTransfer';

  const { data: sellerWarehouses = [], isFetched: sellerWhFetched } = useQuery({
    queryKey: ['seller-warehouses'],
    queryFn: async () => unwrapList<WhRow>(await api.get('/inventory/warehouses')),
    enabled: isGoods,
  });

  const { data: sellerItems = [], isFetched: sellerItemsFetched } = useQuery({
    queryKey: ['seller-items-ic'],
    queryFn: async () => {
      const stock = unwrapList<ItemRow>(await api.get('/inventory/items', { params: { item_type: 'Stock' } }));
      if (stock.length) return stock;
      return unwrapList<ItemRow>(await api.get('/inventory/items'));
    },
    enabled: isGoods,
  });

  const { data: buyerWarehouses = [], isFetched: buyerWhFetched } = useQuery({
    queryKey: ['buyer-warehouses', buyerCompanyId],
    queryFn: () => intercompanyService.affiliateWarehouses(buyerCompanyId!),
    enabled: !!buyerCompanyId && isGoods,
  });

  const { data: buyerItems = [], isFetched: buyerItemsFetched } = useQuery({
    queryKey: ['buyer-items', buyerCompanyId],
    queryFn: () => intercompanyService.affiliateItems(buyerCompanyId!),
    enabled: !!buyerCompanyId && isGoods,
  });

  const buyerItemsByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of buyerItems) map.set(item.item_code, item.id);
    return map;
  }, [buyerItems]);

  const affiliateOptions = useMemo(() => {
    let opts: Opt[] = affiliates.map((a) => ({
      value: a.company_id,
      label: `${a.company_code} — ${a.company_name}`,
    }));
    if (existing?.buyer_company_id) {
      const label =
        existing.buyer_company_code && existing.buyer_company_name
          ? `${existing.buyer_company_code} — ${existing.buyer_company_name}`
          : existing.buyer_company_name || null;
      opts = ensureOption(opts, existing.buyer_company_id, label);
    }
    return opts;
  }, [affiliates, existing]);

  const sellerWhOptions = useMemo(() => {
    let opts: Opt[] = sellerWarehouses.map((w) => ({ value: w.id, label: whLabel(w) }));
    if (existing?.seller_warehouse_id) {
      const label =
        existing.seller_warehouse_code && existing.seller_warehouse_name
          ? `${existing.seller_warehouse_code} — ${existing.seller_warehouse_name}`
          : existing.seller_warehouse_name || null;
      opts = ensureOption(opts, existing.seller_warehouse_id, label);
    }
    return opts;
  }, [sellerWarehouses, existing]);

  const buyerWhOptions = useMemo(() => {
    let opts: Opt[] = buyerWarehouses.map((w) => ({ value: w.id, label: whLabel(w) }));
    if (existing?.buyer_warehouse_id) {
      const label =
        existing.buyer_warehouse_code && existing.buyer_warehouse_name
          ? `${existing.buyer_warehouse_code} — ${existing.buyer_warehouse_name}`
          : existing.buyer_warehouse_name || null;
      opts = ensureOption(opts, existing.buyer_warehouse_id, label);
    }
    return opts;
  }, [buyerWarehouses, existing]);

  const sellerItemOptions = useMemo(() => {
    let opts: Opt[] = sellerItems.map((i) => ({ value: i.id, label: itemLabel(i) }));
    for (const line of existing?.lines || []) {
      if (!line.seller_item_id) continue;
      opts = ensureOption(
        opts,
        line.seller_item_id,
        line.item_code ? `${line.item_code} — ${line.item_description}` : line.item_description
      );
    }
    return opts;
  }, [sellerItems, existing]);

  const buyerItemOptions = useMemo(() => {
    let opts: Opt[] = buyerItems.map((i) => ({ value: i.id, label: itemLabel(i) }));
    for (const line of existing?.lines || []) {
      if (!line.buyer_item_id) continue;
      opts = ensureOption(
        opts,
        line.buyer_item_id,
        line.item_code ? `${line.item_code} — ${line.item_description}` : line.item_description
      );
    }
    return opts;
  }, [buyerItems, existing]);

  const mastersReady =
    !isEdit ||
    (!loadingExisting &&
      !!existing &&
      !loadingAffiliates &&
      (!isGoods || (sellerWhFetched && sellerItemsFetched && buyerWhFetched && buyerItemsFetched)));

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({ transaction_date: dayjs(), transaction_type: 'GoodsTransfer', lines: [{}] });
      return;
    }
    if (!existing || !mastersReady) return;
    if (existing.status !== 'Draft') {
      message.warning(t('onlyDraftCanEdit'));
      navigate(`/fin/intercompany/transactions/${existing.id}`);
      return;
    }
    form.setFieldsValue({
      transaction_type: existing.transaction_type,
      buyer_company_id: existing.buyer_company_id,
      transaction_date: existing.transaction_date ? dayjs(existing.transaction_date) : dayjs(),
      seller_warehouse_id: existing.seller_warehouse_id || undefined,
      buyer_warehouse_id: existing.buyer_warehouse_id || undefined,
      notes: existing.notes,
      lines: (existing.lines || []).map((l) => ({
        seller_item_id: l.seller_item_id || undefined,
        buyer_item_id: l.buyer_item_id || undefined,
        item_description: l.item_description,
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price),
        uom: l.uom,
      })),
    });
  }, [existing, form, isEdit, mastersReady, navigate]);

  const saveMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      isEdit
        ? intercompanyService.updateTransaction(id!, values)
        : intercompanyService.createTransaction(values),
    onSuccess: (tx: IcTransaction) => {
      message.success(isEdit ? t('icDocumentUpdated') : t('icDocumentCreated'));
      navigate(`/fin/intercompany/transactions/${tx.id}`);
    },
    onError: (e: Error) => message.error(e.message || t('failedToSave')),
  });

  const onFinish = (values: Record<string, unknown>) => {
    const lines = (values.lines as Array<Record<string, unknown>>)?.map((line) => ({
      seller_item_id: line.seller_item_id,
      buyer_item_id: line.buyer_item_id,
      quantity: line.quantity,
      unit_price: line.unit_price,
      uom: line.uom,
      item_description: line.item_description,
    }));
    saveMut.mutate({
      transaction_type: values.transaction_type,
      buyer_company_id: values.buyer_company_id,
      seller_warehouse_id: isGoods ? values.seller_warehouse_id : undefined,
      buyer_warehouse_id: isGoods ? values.buyer_warehouse_id : undefined,
      transaction_date: (values.transaction_date as dayjs.Dayjs).format('YYYY-MM-DD'),
      notes: values.notes,
      lines,
    });
  };

  const onSellerItemChange = (sellerItemId: string, fieldName: number) => {
    const item = sellerItems.find((i) => i.id === sellerItemId);
    if (!item) return;
    const buyerItemId = buyerItemsByCode.get(item.item_code);
    const lines = [...(form.getFieldValue('lines') || [])];
    lines[fieldName] = {
      ...lines[fieldName],
      seller_item_id: sellerItemId,
      buyer_item_id: buyerItemId,
      item_description: item.item_name,
      uom: item.base_uom || 'PCS',
      unit_price: item.standard_cost ?? lines[fieldName]?.unit_price ?? 0,
    };
    form.setFieldsValue({ lines });
    if (!buyerItemId) {
      message.warning(
        t('noMatchingBuyerItemCode', { code: item.item_code })
      );
    }
  };

  if (!loadingAffiliates && !affiliates.length && !isEdit) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Title level={4}>{t('noAffiliateCompanies')}</Title>
          <Typography.Paragraph>
            {t('linkAffiliateBeforeIcDocs')}
          </Typography.Paragraph>
          <Button type="primary" onClick={() => navigate('/fin/intercompany/affiliates')}>
            {t('setUpAffiliates')}
          </Button>
        </Card>
      </div>
    );
  }

  if ((isEdit && (loadingExisting || !mastersReady)) || loadingAffiliates) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip={t('loadingDocument')} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, width: '100%', maxWidth: '100%' }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        {isEdit ? t('editIcDocument', { number: existing?.ic_number || t('icDocumentFallback') }) : t('newIntercompanyDocument')}
      </Title>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ lines: [{}], transaction_type: 'GoodsTransfer' }}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={8} lg={6}>
              <Form.Item name="transaction_type" label={t('type')} rules={[{ required: true }]}>
                <Select
                  disabled={isEdit}
                  options={[
                    { value: 'GoodsTransfer', label: t('goodsTransferStockGl') },
                    { value: 'ServiceRecharge', label: t('serviceRechargeGlOnly') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10} lg={10}>
              <Form.Item
                name="buyer_company_id"
                label={t('buyerCompanyAffiliate')}
                rules={[{ required: true }]}
              >
                <Select
                  disabled={isEdit}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('selectBuyer')}
                  options={affiliateOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6} lg={4}>
              <Form.Item name="transaction_date" label={t('date')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {isGoods && (
              <>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    name="seller_warehouse_id"
                    label={t('sellerWarehouse')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('fromWarehouse')}
                      options={sellerWhOptions}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    name="buyer_warehouse_id"
                    label={t('buyerWarehouse')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('toWarehouse')}
                      disabled={!buyerCompanyId}
                      options={buyerWhOptions}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={24}>
              <Form.Item name="notes" label={t('notes')} style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={t('lineItems')} size="small" style={{ marginBottom: 16 }}>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                <Table
                  pagination={false}
                  dataSource={fields}
                  rowKey="key"
                  scroll={{ x: isGoods ? 1100 : 800 }}
                  tableLayout="fixed"
                  columns={[
                    ...(isGoods
                      ? [
                          {
                            title: t('sellerItem'),
                            key: 'seller_item',
                            render: (_: unknown, field: { name: number }) => (
                              <Form.Item
                                name={[field.name, 'seller_item_id']}
                                rules={[{ required: true, message: t('required') }]}
                                style={{ margin: 0 }}
                              >
                                <Select
                                  showSearch
                                  optionFilterProp="label"
                                  placeholder={t('item')}
                                  style={{ width: '100%' }}
                                  options={sellerItemOptions}
                                  onChange={(v) => onSellerItemChange(v, field.name)}
                                />
                              </Form.Item>
                            ),
                          },
                          {
                            title: t('buyerItem'),
                            key: 'buyer_item',
                            render: (_: unknown, field: { name: number }) => (
                              <Form.Item
                                name={[field.name, 'buyer_item_id']}
                                rules={[{ required: true, message: t('required') }]}
                                style={{ margin: 0 }}
                              >
                                <Select
                                  showSearch
                                  optionFilterProp="label"
                                  placeholder={t('buyerItem')}
                                  disabled={!buyerCompanyId}
                                  style={{ width: '100%' }}
                                  options={buyerItemOptions}
                                />
                              </Form.Item>
                            ),
                          },
                        ]
                      : [
                          {
                            title: t('description'),
                            key: 'description',
                            render: (_: unknown, field: { name: number }) => (
                              <Form.Item
                                name={[field.name, 'item_description']}
                                rules={[{ required: true, message: t('required') }]}
                                style={{ margin: 0 }}
                              >
                                <Input placeholder={t('serviceRechargeDescription')} />
                              </Form.Item>
                            ),
                          },
                        ]),
                    {
                      title: t('qty'),
                      key: 'qty',
                      width: 140,
                      render: (_: unknown, field: { name: number }) => (
                        <Form.Item
                          name={[field.name, 'quantity']}
                          rules={[{ required: true, message: t('required') }]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber min={0.0001} style={{ width: '100%' }} controls />
                        </Form.Item>
                      ),
                    },
                    {
                      title: t('unitPrice'),
                      key: 'price',
                      width: 160,
                      render: (_: unknown, field: { name: number }) => (
                        <Form.Item
                          name={[field.name, 'unit_price']}
                          rules={[{ required: true, message: t('required') }]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} controls />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      key: 'remove',
                      width: 48,
                      align: 'center' as const,
                      render: (_: unknown, field: { name: number }) =>
                        fields.length > 1 ? (
                          <MinusCircleOutlined
                            onClick={() => remove(field.name)}
                            style={{ color: '#ff4d4f', fontSize: 16 }}
                          />
                        ) : null,
                    },
                  ]}
                />
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 12 }}>
                  {t('addLine')}
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={saveMut.isPending}>
            {isEdit ? t('saveChanges') : t('createDraft')}
          </Button>
          <Button
            onClick={() =>
              navigate(isEdit ? `/fin/intercompany/transactions/${id}` : '/fin/intercompany/transactions')
            }
          >
            {tc('cancel')}
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default IntercompanyTransactionFormPage;
